import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Send, Users, User, LayoutTemplate } from 'lucide-react';
import { adminApi } from '@/lib/api';

export default function NotificationsAdminPage() {
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('إعلان من بنك العائلة');
  const [privacy, setPrivacy] = useState('standard');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [sending, setSending] = useState(false);
  const [deliveryLog, setDeliveryLog] = useState([]);

  const load = async () => {
    try {
      const [u, n] = await Promise.all([adminApi.get('/admin/users'), adminApi.get('/admin/notifications')]);
      setUsers(Array.isArray(u.data) ? u.data : u.data?.items || []);
      setDeliveryLog(n.data?.items || []);
    } catch { toast.error('تعذر تحميل بيانات الإشعارات'); }
  };
  useEffect(() => { load(); }, []);
  
  const handleSendBroadcast = async () => {
    if (!message.trim()) {
      toast.error('الرجاء كتابة رسالة الإشعار');
      return;
    }
    if (broadcastTarget === 'specific' && !selectedUser) return toast.error('اختر مستخدمًا مستهدفًا');
    try {
      setSending(true);
      const { data } = await adminApi.post('/admin/notifications', { title, message, privacy, target: broadcastTarget, user_ids: selectedUser ? [selectedUser] : [] });
      toast.success(`تم إرسال الإشعار إلى ${data.recipients} مستخدم`);
      setMessage(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'تعذر إرسال الإشعار'); }
    finally { setSending(false); }
  };

  const templates = [
    { id: 1, name: 'انتهاء الاشتراك قريباً', text: 'عزيزي المستخدم، اشتراكك سينتهي بعد 3 أيام. جدد الآن لتجنب انقطاع الخدمة.' },
    { id: 2, name: 'نجاح التجديد', text: 'تم تجديد اشتراكك بنجاح! شكراً لاستخدامك بنك العائلة.' },
    { id: 3, name: 'اكتمال هدف', text: 'تهانينا! لقد أكملت هدفك المالي. استمر في التوفير!' },
    { id: 4, name: 'إصدار استرداد', text: 'تمت الموافقة على طلب الاسترداد الخاص بك وإرجاع المبلغ إلى رصيدك.' }
  ];

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h2 className="text-xl font-black flex items-center gap-2">
          <Bell className="w-6 h-6 text-orange-500" />
          إدارة الإشعارات
        </h2>
        <p className="text-sm text-[var(--c-muted)]">إرسال إشعارات جماعية وإدارة القوالب الآلية</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-[var(--c-border)] p-6">
          <h3 className="font-bold flex items-center gap-2 mb-4 text-[var(--c-navy)]">
            <Send className="w-5 h-5 text-blue-500" />
            إرسال إشعار يدوي (Broadcast)
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="label-pro">الجمهور المستهدف</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button 
                  onClick={() => setBroadcastTarget('all')} 
                  className={`p-2 rounded-xl text-sm font-bold border flex justify-center gap-2 ${broadcastTarget === 'all' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 text-gray-600'}`}
                >
                  <Users className="w-4 h-4"/> الجميع
                </button>
                <button 
                  onClick={() => setBroadcastTarget('bank')} 
                  className={`p-2 rounded-xl text-sm font-bold border flex justify-center gap-2 ${broadcastTarget === 'bank' ? 'bg-slate-100 border-slate-500 text-slate-700' : 'border-gray-200 text-gray-600'}`}
                >
                  بنك العائلة فقط
                </button>
                <button 
                  onClick={() => setBroadcastTarget('bank_amwali_plus')} 
                  className={`p-2 rounded-xl text-sm font-bold border flex justify-center gap-2 ${broadcastTarget === 'bank_amwali_plus' ? 'bg-yellow-50 border-yellow-500 text-yellow-700' : 'border-gray-200 text-gray-600'}`}
                >
                  باقة أموالي + فقط
                </button>
                <button 
                  onClick={() => setBroadcastTarget('specific')} 
                  className={`p-2 rounded-xl text-sm font-bold border flex justify-center gap-2 ${broadcastTarget === 'specific' ? 'bg-purple-50 border-purple-500 text-purple-700' : 'border-gray-200 text-gray-600'}`}
                >
                  <User className="w-4 h-4"/> مستخدم محدد
                </button>
              </div>
            </div>

            {broadcastTarget === 'specific' && (
              <div>
                <label className="label-pro">المستخدم المستهدف</label>
                <select className="input-pro" value={selectedUser} onChange={e=>setSelectedUser(e.target.value)}><option value="">اختر مستخدمًا</option>{users.map(u=><option key={u.id} value={u.id}>{u.display_name || u.username} (@{u.username})</option>)}</select>
              </div>
            )}

            <div>
              <label className="label-pro">عنوان الإشعار</label>
              <input value={title} onChange={e => setTitle(e.target.value)} className="input-pro" maxLength="120" />
            </div>
            <div>
              <label className="label-pro">نص الإشعار</label>
              <textarea 
                value={message}
                onChange={e => setMessage(e.target.value)}
                className="input-pro" 
                rows="4" 
                placeholder="اكتب رسالتك هنا..."
              ></textarea>
            </div>
            <label className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700"><input type="checkbox" checked={privacy === 'sensitive'} onChange={e => setPrivacy(e.target.checked ? 'sensitive' : 'standard')} /><span><b>إخفاء التفاصيل على شاشة القفل</b><br/>يُحفظ النص الكامل داخل التطبيق، لكن يظهر في إشعار النظام نص عام فقط.</span></label>

            <button disabled={sending} onClick={handleSendBroadcast} className="btn-navy w-full flex justify-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" /> إرسال الإشعار
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-[var(--c-border)] p-6">
          <h3 className="font-bold flex items-center gap-2 mb-4 text-[var(--c-navy)]">
            <LayoutTemplate className="w-5 h-5 text-[var(--c-gold)]" />
            قوالب الإشعارات الآلية
          </h3>
          <p className="text-xs text-[var(--c-muted)] mb-4">هذه الإشعارات يتم إرسالها تلقائياً من قبل النظام حسب الأحداث.</p>
          
          <div className="space-y-4">
            {templates.map(t => (
              <div key={t.id} className="p-3 border border-slate-200 rounded-xl bg-slate-50">
                <div className="font-bold text-sm mb-2">{t.name}</div>
                <textarea 
                  defaultValue={t.text} 
                  className="input-pro text-xs text-[var(--c-muted)]" 
                  rows="2"
                ></textarea>
                <button className="text-xs text-blue-600 font-bold mt-2 hover:underline">حفظ التعديل</button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-[var(--c-border)] p-6"><h3 className="font-bold mb-4">سجل التسليم داخل التطبيق</h3>{deliveryLog.length===0?<p className="text-sm text-slate-500">لا توجد إشعارات مرسلة.</p>:<div className="space-y-2">{deliveryLog.slice(0,20).map(n=><div key={n.id} className="p-3 border rounded-xl flex flex-wrap justify-between gap-2 text-sm"><span>{n.message}</span><span className="text-slate-500">{n.recipient_ids?.length || 0} مستلم · قُرئ بواسطة {n.read_by?.length || 0}</span></div>)}</div>}</div>
    </div>
  );
}
